/**
 * Bug 2 (Corbin, 2026-10-17) — "there is an intended disposal and an actual disposal ... the
 * actual disposal should have the price assigned ... Nothing should be lost."
 *
 * A ticket flagged DISPOSAL_NOT_RECORDED went to a disposal (its job type ends in one) but no
 * actual disposal was ever recorded, so it cannot be priced or approved. The reviewer picks
 * where the water actually went and records it (POST /v1/inreview/:id/actual-disposal,
 * audited). The intended place is offered first as a hint, never assumed.
 *
 * 0216 (Corbin 2026-10-18): or the reviewer records that NO disposal happened, with a reason of
 * 10+ characters (POST /v1/inreview/:id/no-disposal, audited; no disposal fee).
 */

import * as React from 'react';
import type { DisposalRecommendationsResponse } from '../jobs/destination-options';
import { useExclusionPlaces } from '../sites-operators/disposal-exclusions';
import { useRecordActualDisposal, useRecordNoDisposal, useUndoNoDisposal } from './use-inreview';

const MIN_REASON = 10;

const KIND_LABEL: Record<'FACILITY' | 'LOCATION' | 'WELL', string> = {
  FACILITY: 'facility',
  LOCATION: 'drop-off location',
  WELL: 'disposal well',
};

export function RecordActualDisposal({
  jobId,
  intended,
  detail,
}: {
  jobId: string;
  intended: string | null;
  detail: string;
}): React.JSX.Element {
  const places = useExclusionPlaces(true);
  const record = useRecordActualDisposal(jobId);
  const [value, setValue] = React.useState('');
  const none = useRecordNoDisposal(jobId);
  const [noneOpen, setNoneOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const reasonOk = reason.trim().length >= MIN_REASON;

  const options = React.useMemo(() => {
    const list: DisposalRecommendationsResponse['options'] = places.data?.options ?? [];
    // The intended place first, when it is one of the usable places (a hint, not a default).
    return [...list].sort((a, b) =>
      a.name === intended ? -1 : b.name === intended ? 1 : a.name.localeCompare(b.name),
    );
  }, [places.data, intended]);

  const chosen = options.find((o) => `${o.kind}:${o.id}` === value);

  return (
    <div
      data-testid="record-actual-disposal"
      style={{
        border: '1px solid rgba(243, 139, 139,0.4)',
        background: 'rgba(243, 139, 139,0.08)',
        borderRadius: '12px',
        padding: '12px',
        marginBottom: '12px',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>Disposal not recorded</div>
      <div style={{ fontSize: '13px', marginBottom: '8px' }}>{detail}</div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="actual-disposal-select" style={{ fontSize: '13px' }}>
          Where did the water actually go?
        </label>
        <select
          id="actual-disposal-select"
          value={value}
          onChange={(e): void => setValue(e.target.value)}
          disabled={places.isLoading || record.isPending}
          data-testid="actual-disposal-select"
        >
          <option value="">Choose the actual disposal…</option>
          {options.map((o) => (
            <option key={`${o.kind}:${o.id}`} value={`${o.kind}:${o.id}`}>
              {o.name} ({KIND_LABEL[o.kind]}){o.name === intended ? ' — intended' : ''}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={chosen === undefined || record.isPending}
          onClick={(): void => {
            if (chosen !== undefined) {
              record.mutate({ target: { kind: chosen.kind, id: chosen.id } });
            }
          }}
          data-testid="record-actual-disposal-button"
        >
          Record actual disposal
        </button>
        {!noneOpen && (
          <button
            type="button"
            onClick={(): void => setNoneOpen(true)}
            disabled={none.isPending}
            data-testid="no-disposal-open"
          >
            No disposal happened
          </button>
        )}
      </div>
      {noneOpen && (
        <div style={{ marginTop: '8px' }} data-testid="no-disposal-form">
          <label htmlFor="no-disposal-reason" style={{ fontSize: '13px', display: 'block' }}>
            Why was there no disposal? (at least {MIN_REASON} characters)
          </label>
          <textarea
            id="no-disposal-reason"
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            rows={2}
            maxLength={500}
            disabled={none.isPending}
            style={{ width: '100%', marginTop: '4px' }}
            data-testid="no-disposal-reason"
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
            <button
              type="button"
              disabled={!reasonOk || none.isPending}
              onClick={(): void => none.mutate({ reason: reason.trim() })}
              data-testid="no-disposal-confirm"
            >
              Record no disposal
            </button>
            <button
              type="button"
              disabled={none.isPending}
              onClick={(): void => {
                setNoneOpen(false);
                setReason('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {record.isError && (
        <div
          role="alert"
          style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '6px' }}
        >
          Could not record the disposal. Check the place is still active and try again.
        </div>
      )}
      {none.isError && (
        <div
          role="alert"
          style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '6px' }}
        >
          Could not record that no disposal happened. Try again.
        </div>
      )}
    </div>
  );
}

/**
 * #920 review (LOW a) — the office recorded "no disposal happened" by mistake: take it back with a
 * reason (10+ characters, audited). The ticket is DISPOSAL_NOT_RECORDED again, and the panel above
 * offers to record the actual disposal.
 */
export function UndoNoDisposal({
  jobId,
  reason: recorded,
}: {
  jobId: string;
  reason: string | null;
}): React.JSX.Element {
  const undo = useUndoNoDisposal(jobId);
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const reasonOk = reason.trim().length >= MIN_REASON;

  return (
    <div
      data-testid="no-disposal-recorded"
      style={{
        border: '1px solid rgba(125, 130, 142,0.4)',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>No disposal happened</div>
      {recorded !== null && <div style={{ fontSize: '13px', marginBottom: '8px' }}>{recorded}</div>}
      {!open ? (
        <button
          type="button"
          onClick={(): void => setOpen(true)}
          data-testid="undo-no-disposal-open"
        >
          Undo no disposal
        </button>
      ) : (
        <div>
          <label htmlFor="undo-no-disposal-reason" style={{ fontSize: '13px', display: 'block' }}>
            Why undo it? (at least {MIN_REASON} characters)
          </label>
          <textarea
            id="undo-no-disposal-reason"
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            rows={2}
            maxLength={500}
            disabled={undo.isPending}
            style={{ width: '100%', marginTop: '4px' }}
            data-testid="undo-no-disposal-reason"
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
            <button
              type="button"
              disabled={!reasonOk || undo.isPending}
              onClick={(): void => undo.mutate({ reason: reason.trim() })}
              data-testid="undo-no-disposal-confirm"
            >
              Undo no disposal
            </button>
            <button
              type="button"
              disabled={undo.isPending}
              onClick={(): void => {
                setOpen(false);
                setReason('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {undo.isError && (
        <div
          role="alert"
          style={{ color: 'var(--color-danger)', fontSize: '13px', marginTop: '6px' }}
        >
          Could not undo it. Try again.
        </div>
      )}
    </div>
  );
}
